import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ComingSoonComponent } from "../../../../shared/components/coming-soon/coming-soon.component";
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';
import { CommonModule } from '@angular/common';
import { TippyDirective } from '@ngneat/helipopper';
import { PremiumTooltipComponent } from '../../../../shared/components/premium-tooltip/premium-tooltip.component';
import { tooltips } from '../../../../utils/tooltips';
import { AuthorizationService } from '../../../../services/authorization.service';
import { PolarisModelVersion } from '../../../../services/polaris/model';
import { PolarisModelSelectorComponent } from '../polaris-model-selector/polaris-model-selector.component';

@Component({
  selector: 'app-copilot-prompt-bar',
  imports: [
    ReactiveFormsModule,
    ComingSoonComponent,
    AssetVersionPipe,
    CommonModule,
    TippyDirective,
    PremiumTooltipComponent,
    PolarisModelSelectorComponent
  ],
  templateUrl: './copilot-prompt-bar.component.html',
  styleUrl: './copilot-prompt-bar.component.scss'
})

export class CopilotPromptBarComponent implements AfterViewInit {
  @Input() promptControl!: FormControl;
  @Input() maxHeight = 180;
  @Input() shouldDisplayExtraButtons = false;
  @Input() placeholder = "Ask me anything about your brand, campaigns, or competitors";
  @Input() showModelSelector = false;
  @Input() selectedModel: PolarisModelVersion = 'v2';
  @Input() modelSelectorDisabled = false;
  @Input() multilineThreshold = 50;
  @Output() sendMessage = new EventEmitter<string>();
  @Output() modelChange = new EventEmitter<PolarisModelVersion>();
  @ViewChild('promptInput', { static: false }) promptInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('promptBarContainer', { static: false }) promptBarContainer!: ElementRef<HTMLDivElement>;

  private authorizationService = inject(AuthorizationService);
  protected readonly tooltips = tooltips;
  protected isExpiredUser: boolean;
  protected isGalleryHovered = false;
  protected isClipHovered = false;

  private readonly lineHeight = 20;
  private readonly maxRows = 4;
  private readonly singleLineHeight = 24;
  private readonly verticalPadding = 8;
  private readonly cap = this.maxRows * this.lineHeight + this.verticalPadding * 2;

  constructor() {
    this.isExpiredUser = this.authorizationService.isExpiredUser;
  }

  ngAfterViewInit(): void {
    this.focusPromptInput();
    this.promptInput.nativeElement.style.height = this.singleLineHeight + 'px';
    this.promptBarContainer.nativeElement.style.maxHeight = this.maxHeight + 'px';
  }

  focusPromptInput(): void {
    if (this.isExpiredUser) return;

    this.promptInput?.nativeElement.focus();
  }

  resizeTextArea() {
    if (this.isExpiredUser) return;

    const el = this.promptInput.nativeElement;
    const isMultiline = el.value.length > this.multilineThreshold;
    this.promptBarContainer.nativeElement.classList.toggle('multiline', isMultiline);

    const pad = isMultiline ? this.verticalPadding * 2 : 0;
    el.style.overflowY = 'hidden'; // a scrollbar must not narrow the text mid-measure
    el.style.height = '0px';
    const contentHeight = el.scrollHeight - pad;
    el.style.height = `${Math.min(Math.max(contentHeight, this.singleLineHeight) + pad, this.cap)}px`;
    el.style.overflowY = '';
    this.updateFade();
  }

  updateFade(): void {
    if (this.isExpiredUser) return;

    const el = this.promptInput.nativeElement;
    const fadeTop = el.scrollTop > 1;
    const fadeBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;

    let mask = 'none';
    if (fadeTop || fadeBottom) {
      const stops = [
        fadeTop ? 'transparent 0%, #000 12%' : '#000 0%',
        fadeBottom ? '#000 88%, transparent 100%' : '#000 100%',
      ];
      mask = `linear-gradient(to bottom, ${stops.join(', ')})`;
    }
    el.style.setProperty('mask-image', mask);
    el.style.setProperty('-webkit-mask-image', mask);
  }

  onSend(event?: Event): void {
    if (this.isExpiredUser) return;

    event?.preventDefault();

    if (this.promptControl.value?.trim()) {
      this.sendMessage.emit(this.promptControl.value);
      this.promptControl.setValue('');
      this.resizeTextArea();
    }
  }

}
